/**
 * Sprite bối cảnh charforge: tile sàn + vật thể 3 khu
 * (Phố chợ, Bãi biển, Chợ đêm) + 3 món nội thất. 48×48, vẽ bằng code.
 *
 * Tile sàn vẽ pattern trung tính ở mép để ghép lưới không bị "vỡ".
 * Chi tiết trang trí (sỏi, vân sóng, hạt cát) dùng RNG đã seed theo ID.
 */
import {
  BANG_MAU,
  Raster,
  duongThang,
  rect,
  sangLen,
  toiDi,
  tron,
  tronVien,
  vien,
  type VeSprite,
} from '../raster.js';

/** Rải `n` hạt pixel ngẫu nhiên (seeded) lên raster. */
function raiHat(r: Raster, rng: () => number, n: number, mau: string, vung?: number): void {
  const g = vung ?? 48;
  for (let i = 0; i < n; i++) {
    r.px(Math.floor(rng() * g), Math.floor(rng() * g), mau);
  }
}

/** Tile sàn: nền + lưới gạch. */
function tileGach(r: Raster, rng: () => number, nen: string, mach: string, o = 12): void {
  r.nen(nen);
  for (let y = 0; y < 48; y += o) {
    for (let x = 0; x < 48; x += o) {
      vien(r, x, y, o, o, mach, 1);
    }
  }
  raiHat(r, rng, 24, toiDi(nen, 0.92));
}

/** 19 sprite bối cảnh: id manifest → hàm vẽ. */
export const VE_BOI_CANH: Record<string, VeSprite> = {
  // --- Phố chợ ---
  'san-pho-cho': (r, rng) => {
    tileGach(r, rng, '#c99a5e', '#a87f4e');
  },
  'via-he': (r, rng) => {
    tileGach(r, rng, '#9a9a9a', '#7f7f7f', 16);
    raiHat(r, rng, 30, '#8a8a8a');
  },
  'bien-hieu': (r) => {
    r.nen('#5e4632');
    // Dây treo.
    rect(r, 10, 0, 3, 10, '#3a2a1e');
    rect(r, 35, 0, 3, 10, '#3a2a1e');
    // Bảng gỗ.
    rect(r, 6, 10, 36, 26, '#8a5a2e');
    vien(r, 6, 10, 36, 26, '#5e3a1a', 2);
    rect(r, 8, 12, 32, 3, sangLen('#8a5a2e', 0.2));
    // Họa tiết giỏ hàng (thay chữ).
    rect(r, 16, 20, 16, 9, '#6e3a1a');
    rect(r, 16, 18, 16, 3, '#6e3a1a');
    duongThang(r, 18, 18, 30, 10, '#ffd75e');
    for (let i = 0; i < 3; i++) rect(r, 19 + i * 5, 21, 2, 7, '#ffd75e');
    rect(r, 14, 31, 20, 2, '#ffd75e'); // Gạch chân trang trí.
  },
  'cua-ra-vao': (r) => {
    r.nen('#5e4632');
    // Khung cửa.
    rect(r, 10, 4, 28, 42, '#8a5a2e');
    // Lòng cửa tối (đang mở).
    rect(r, 14, 8, 20, 38, '#2e2118');
    // Cánh cửa mở hé.
    rect(r, 14, 8, 7, 38, '#a8703a');
    rect(r, 14, 8, 2, 38, sangLen('#a8703a', 0.2));
    // Tay nắm + mái hiên.
    tron(r, 27, 26, 2, '#ffd75e');
    rect(r, 8, 0, 32, 5, '#d94f4f');
    for (let i = 0; i < 8; i++) rect(r, 8 + i * 4, 0, 2, 5, '#f0f0f0');
  },
  'cay-xanh': (r, rng) => {
    r.nen('#9db87a');
    raiHat(r, rng, 20, '#8aa868');
    // Thân.
    rect(r, 21, 28, 6, 16, '#6b4a2a');
    rect(r, 21, 28, 2, 16, '#7f5a36');
    // Tán lá 3 tầng.
    tron(r, 24, 22, 10, '#3fae6a');
    tron(r, 17, 26, 7, '#3fae6a');
    tron(r, 31, 26, 7, '#3fae6a');
    tron(r, 21, 18, 5, '#58c878');
    tron(r, 28, 20, 4, '#58c878');
    // Bồn cây.
    rect(r, 15, 42, 18, 5, '#8a8a8a');
    vien(r, 15, 42, 18, 5, '#6e6e6e', 1);
  },
  'thung-rac': (r) => {
    r.nen('#9db87a');
    rect(r, 16, 16, 16, 26, '#4a7a5e');
    rect(r, 16, 16, 4, 26, '#5e9680');
    rect(r, 28, 16, 4, 26, '#3a5e4a');
    // Nắp + gờ.
    rect(r, 14, 12, 20, 5, '#3a5e4a');
    rect(r, 22, 8, 4, 4, '#3a5e4a');
    for (let i = 0; i < 3; i++) rect(r, 18, 22 + i * 6, 12, 2, '#3a5e4a');
  },
  'den-duong': (r) => {
    r.nen('#8a9ab0');
    // Cột đèn.
    rect(r, 22, 14, 4, 32, '#3a3a3a');
    rect(r, 22, 14, 1, 32, '#5a5a5a');
    // Đế.
    rect(r, 18, 42, 12, 5, '#2a2a2a');
    // Tay ngang + chụp đèn.
    rect(r, 22, 10, 12, 3, '#3a3a3a');
    rect(r, 30, 12, 8, 6, '#2a2a2a');
    // Bóng đèn sáng.
    rect(r, 31, 18, 6, 4, '#ffe45e');
    tron(r, 34, 24, 6, '#fff0a8');
    tron(r, 34, 24, 9, '#fff8d0');
  },

  // --- Bãi biển ---
  'san-bai-bien': (r, rng) => {
    r.nen('#ecd9a0');
    // Vân sóng cát.
    for (let i = 0; i < 4; i++) {
      const y = 8 + i * 11;
      for (let x = 0; x < 48; x += 2) {
        const dy = Math.round(Math.sin(x / 6 + i) * 2);
        r.px(x, y + dy, '#d9bd7f');
      }
    }
    raiHat(r, rng, 26, '#d9bd7f');
    raiHat(r, rng, 10, BANG_MAU.trang);
  },
  'nuoc-bien': (r, rng) => {
    r.nen('#4f9fd9');
    // Sóng trắng.
    for (let i = 0; i < 3; i++) {
      const y = 10 + i * 13;
      for (let x = 0; x < 48; x += 3) {
        const dy = Math.round(Math.sin(x / 5 + i * 2) * 2);
        r.px(x, y + dy, '#bfe0f5');
        r.px(x + 1, y + dy, '#bfe0f5');
      }
    }
    raiHat(r, rng, 16, '#3f8fc9');
  },
  'quay-ca-phe': (r) => {
    r.nen('#e8d5a8');
    // Quầy.
    rect(r, 6, 28, 36, 16, '#6e4a2a');
    rect(r, 6, 28, 36, 4, '#a87f4e');
    rect(r, 6, 28, 5, 16, sangLen('#6e4a2a', 0.2));
    // Máy pha.
    rect(r, 10, 12, 12, 16, '#444444');
    rect(r, 10, 12, 12, 4, '#5e5e5e');
    rect(r, 13, 20, 4, 6, '#c9a25e');
    // Ly + hơi nóng.
    rect(r, 30, 20, 7, 8, BANG_MAU.trang);
    rect(r, 30, 20, 7, 2, '#d9d9d9');
    duongThang(r, 32, 16, 30, 10, '#ffffff');
    duongThang(r, 35, 16, 37, 10, '#ffffff');
    // Dù che.
    duongThang(r, 24, 0, 6, 10, '#d94f4f');
    duongThang(r, 24, 0, 42, 10, '#d94f4f');
    rect(r, 6, 10, 36, 3, '#f0f0f0');
  },
  'cay-dua': (r, rng) => {
    r.nen('#bfe0f5');
    raiHat(r, rng, 12, '#a8d0f0');
    // Thân nghiêng (khúc).
    for (let i = 0; i < 7; i++) {
      rect(r, 20 + i, 40 - i * 4, 6, 5, i % 2 === 0 ? '#8a5a2e' : '#7a4e26');
    }
    // Tán lá.
    const la = '#3fae6a';
    duongThang(r, 27, 16, 10, 8, la);
    duongThang(r, 27, 16, 12, 20, la);
    duongThang(r, 27, 16, 44, 10, la);
    duongThang(r, 27, 16, 42, 22, la);
    duongThang(r, 27, 16, 27, 4, la);
    // Trái dừa.
    tron(r, 24, 20, 4, '#6b4a2a');
    tron(r, 31, 21, 4, '#6b4a2a');
    r.px(23, 19, sangLen('#6b4a2a', 0.3));
  },
  'ghe-bien': (r) => {
    r.nen('#ecd9a0');
    // Ghế nằm: khung + vải sọc.
    duongThang(r, 10, 36, 38, 20, '#8a5a2e');
    duongThang(r, 10, 36, 14, 44, '#8a5a2e');
    duongThang(r, 38, 20, 34, 12, '#8a5a2e');
    for (let i = 0; i < 6; i++) {
      duongThang(
        r,
        14 + i * 4,
        34 - i * 2,
        17 + i * 4,
        34 - i * 2,
        i % 2 === 0 ? '#d94f4f' : '#f0f0f0',
      );
      duongThang(
        r,
        14 + i * 4,
        33 - i * 2,
        17 + i * 4,
        33 - i * 2,
        i % 2 === 0 ? '#d94f4f' : '#f0f0f0',
      );
    }
    // Dù nhỏ.
    duongThang(r, 36, 40, 36, 22, '#6b4a2a');
    duongThang(r, 36, 22, 26, 30, '#ffe45e');
    duongThang(r, 36, 22, 46, 30, '#ffe45e');
  },

  // --- Chợ đêm ---
  'san-cho-dem': (r, rng) => {
    r.nen('#2a2438');
    // Ánh neon phản chiếu.
    const neon = ['#ff5ef1', '#5effd1', '#ffe45e'];
    for (let i = 0; i < 12; i++) {
      const x = Math.floor(rng() * 44) + 2;
      const y = Math.floor(rng() * 44) + 2;
      r.px(x, y, neon[i % 3] as string);
      r.px(x + 1, y, neon[i % 3] as string);
    }
    raiHat(r, rng, 20, '#3a3450');
  },
  'den-long': (r) => {
    r.nen('#2a2438');
    // Dây treo.
    rect(r, 23, 0, 2, 8, '#1a1626');
    // Đèn lồng đỏ.
    tron(r, 24, 20, 10, '#d94f4f');
    tron(r, 24, 20, 7, '#e86a6a');
    for (let i = -2; i <= 2; i++) rect(r, 24 + i * 4 - 1, 12, 2, 16, '#b33a3a');
    rect(r, 20, 8, 8, 4, '#8a2a2a'); // Nắp.
    rect(r, 20, 28, 8, 4, '#8a2a2a'); // Đế.
    duongThang(r, 24, 32, 24, 40, '#ffd75e'); // Tua rua.
    // Hào quang.
    tronVien(r, 24, 20, 13, '#ff9a8a', 1);
    tronVien(r, 24, 20, 16, '#5e2a3a', 1);
  },
  'xe-day': (r) => {
    r.nen('#2a2438');
    // Thùng xe.
    rect(r, 8, 24, 32, 12, '#8a5a2e');
    rect(r, 8, 24, 32, 3, '#a8703a');
    rect(r, 8, 24, 4, 12, sangLen('#8a5a2e', 0.2));
    // Hàng hóa (trái cây).
    tron(r, 16, 20, 4, '#ff8a3f');
    tron(r, 24, 19, 4, '#ffe45e');
    tron(r, 32, 20, 4, '#d94f4f');
    // Bánh xe + tay đẩy.
    tron(r, 16, 40, 4, '#222222');
    tron(r, 32, 40, 4, '#222222');
    tron(r, 16, 40, 1, '#8a8a8a');
    tron(r, 32, 40, 1, '#8a8a8a');
    duongThang(r, 40, 26, 46, 18, '#6b4a2a');
    // Đèn xe.
    tron(r, 10, 22, 2, '#ffe45e');
  },
  'gian-hang': (r) => {
    r.nen('#2a2438');
    // Cột.
    rect(r, 8, 16, 4, 28, '#6b4a2a');
    rect(r, 36, 16, 4, 28, '#6b4a2a');
    // Mái sọc.
    for (let i = 0; i < 6; i++) {
      rect(r, 4 + i * 7, 10, 7, 7, i % 2 === 0 ? '#d94f4f' : '#f0f0f0');
    }
    rect(r, 4, 16, 40, 2, '#8a2a2a');
    // Mặt bàn + hàng.
    rect(r, 8, 30, 32, 12, '#8a5a2e');
    rect(r, 8, 30, 32, 3, '#a8703a');
    tron(r, 16, 26, 3, '#ffe45e');
    rect(r, 24, 22, 6, 8, '#4f9fd9');
    tron(r, 34, 26, 3, '#ff8a3f');
    // Đèn lồng mini treo mái.
    tron(r, 24, 6, 3, '#ff8a6a');
  },

  // --- Nội thất (vẽ lại pixel-art, giữ bố cục cũ) ---
  'ke-go': (r) => {
    r.nen('#8a5a2e');
    vien(r, 3, 3, 42, 42, '#5e3a1a', 3);
    const mauHang = ['#d94f4f', '#4f9fd9', '#58b368'];
    for (let i = 0; i < 3; i++) {
      const y = 10 + i * 12;
      rect(r, 6, y + 8, 36, 3, '#5e3a1a'); // Đợt.
      for (let j = 0; j < 4; j++) {
        const mau = mauHang[(i + j) % 3] as string;
        const x = 8 + j * 9;
        rect(r, x, y, 7, 8, mau); // Hộp hàng.
        rect(r, x, y, 7, 2, sangLen(mau, 0.25));
      }
    }
  },
  'tu-lanh': (r) => {
    r.nen('#bcd6e8');
    rect(r, 10, 5, 28, 38, '#eef4f8');
    vien(r, 10, 5, 28, 38, '#7a97ad', 2);
    rect(r, 12, 7, 4, 34, BANG_MAU.trang); // Bóng sáng.
    // Ngăn đá / ngăn mát.
    rect(r, 12, 16, 24, 2, '#7a97ad');
    rect(r, 31, 9, 3, 10, '#7a97ad'); // Tay nắm trên.
    rect(r, 31, 21, 3, 12, '#7a97ad'); // Tay nắm dưới.
    // Bông tuyết.
    duongThang(r, 22, 8, 22, 14, '#4f9fd9');
    duongThang(r, 19, 11, 25, 11, '#4f9fd9');
    // Chai nước trong tủ (gợi ý).
    rect(r, 15, 24, 5, 12, '#bfe0f5');
    rect(r, 24, 24, 5, 12, '#cfe8b8');
  },
  'quay-thu-ngan': (r) => {
    r.nen('#a06a3a');
    // Thân quầy.
    rect(r, 5, 26, 38, 18, '#7a4e26');
    rect(r, 5, 26, 38, 4, '#c99a5e');
    rect(r, 5, 26, 5, 18, sangLen('#7a4e26', 0.2));
    // Vân gỗ.
    for (let i = 0; i < 3; i++) rect(r, 12 + i * 10, 32, 6, 2, '#5e3a1a');
    // Máy tính tiền.
    rect(r, 27, 12, 12, 14, '#333333');
    rect(r, 27, 12, 12, 3, '#4a4a4a');
    rect(r, 29, 16, 8, 4, '#7fe07f'); // Màn hình.
    r.px(30, 17, '#2a6e2a');
    r.px(32, 17, '#2a6e2a');
    // Hóa đơn.
    rect(r, 8, 14, 8, 12, BANG_MAU.trang);
    duongThang(r, 10, 18, 14, 18, '#9a9a9a');
    duongThang(r, 10, 21, 14, 21, '#9a9a9a');
  },
};
